import { ExperimentType, ExtensionDetailsType } from "../../../../Types";
import { FIELD_VALIDATORS } from "./experimentDetailsValidators";

export const EXPERIMENT_FIELDS = [
  "featureName",
  "featureKey",
  "featureDescription",
  "extensionFilters",
  "analysisTags",
  "enableExtensionPrefix",
];

export const ADVANCED_FILTERS_FIELDS = [
  "tenantFilters",
  "extensionVersionFilters",
  "environmentFilters",
];

export const VARIANT_FIELDS = ["variantDetails"];

export const FIELD_TOOLTIPS: Record<string, string> = {
  featureKey:
    "Lowercase letters, numbers, hyphens. Must start with a letter or number. Cannot start with 'd-' or '-'.",
  serviceIdentifier: "Must be at least 36 characters.",
  serviceGroup: "Must be at least 4 characters.",
  displayName: "Must be at least 5 characters.",
  extensionFilters:
    "At least one extension filter is required. Filters must be unique and cannot have trailing whitespace.",
  analysisTags:
    "Must be a 2D array. For PROD, must have 6 arrays (one for each slot).",
  name: "Cannot start with 'd-' or '-'. Only lowercase letters, numbers, hyphens, and underscores are allowed.",
};

/**
 * Checks if the given featureName and featureKey are unique among all experiments.
 *
 * @param experiments - The experiments object from the store
 * @param featureName - The feature name to check
 * @param featureKey - The feature key to check
 * @param options - { isClone, isNewExperiment, fileData, extensionPrefix } for context
 * @returns Object with nameError and keyError strings if not unique, otherwise null values
 */
export function validateUniqueFeature(
  experiments: ExperimentType[],
  data: ExperimentType,
  options: {
    isCreate?: boolean;
    isClone?: boolean;
    fileData: ExperimentType;
    extensionPrefix?: string;
  },
): Record<string, string | null> {
  const { isCreate, isClone, fileData, extensionPrefix = "" } = options;

  // Filter experiments for uniqueness check
  const experimentsToCheck = experiments.filter((exp: ExperimentType) => {
    // If we're creating a new experiment, check against all existing experiments
    if (isCreate || isClone) return true;
    // If we're updating an existing experiment, exclude the current one being edited
    if (fileData && exp.__fullpath === fileData.__fullpath) return false;
    return true;
  });
  const { featureName, featureKey, variantDetails, enableExtensionPrefix } =
    data;

  // Skip validation if featureName or featureKey are empty/undefined
  if (!featureName || !featureKey) {
    return {
      featureName: "",
      featureKey: "",
    };
  }

  const nameExists = experimentsToCheck.some(
    (exp: ExperimentType) =>
      exp.featureName?.trim().toLowerCase() ===
      featureName?.trim().toLowerCase(),
  );

  // When comparing featureKey, add prefix if enableExtensionPrefix is true
  const featureKeyToCompare = enableExtensionPrefix
    ? `${extensionPrefix}${featureKey}`.trim().toLowerCase()
    : featureKey?.trim().toLowerCase();

  const keyExists = experimentsToCheck.some(
    (exp: ExperimentType) =>
      exp.featureKey?.trim().toLowerCase() === featureKeyToCompare,
  );
  const variantNameExists = experimentsToCheck.some((exp: ExperimentType) => {
    const variantNames =
      exp.variantDetails?.map(({ name }) => name?.trim().toLowerCase()) || [];
    const currentVariantNames =
      variantDetails?.map(({ name }) => name?.trim().toLowerCase()) || [];
    const allNames = [...variantNames, ...currentVariantNames];
    return new Set(allNames).size !== allNames.length;
  });

  return {
    featureName: nameExists ? "Feature Name must be unique" : null,
    featureKey: keyExists ? "Feature Key must be unique" : null,
    variantDetails: variantNameExists
      ? "Variant names must be unique across all experiments"
      : null,
  };
}

type ValidateParams = {
  data: ExperimentType;
  setErrors: (errors: Record<string, string[]>) => void;
  experiments: ExperimentType[];
  isCreate?: boolean;
  isClone?: boolean;
  extensionPrefix?: string;
  extensionConfig: ExtensionDetailsType | null;
};

export const validate = ({
  data,
  setErrors,
  experiments,
  isCreate,
  isClone,
  extensionPrefix,
  extensionConfig,
}: ValidateParams) => {
  const newErrors: Record<string, string[]> = {};
  const validationFields = Object.keys(FIELD_VALIDATORS);
  for (const key of validationFields) {
    const validator = FIELD_VALIDATORS[key];
    if (validator) {
      const fieldErrors = validator(data[key], data, {
        extensionPrefix,
        extensionConfig,
      });
      if (fieldErrors && fieldErrors.length > 0) {
        newErrors[key] = fieldErrors;
      }
    }
  }

  // Validate optional advanced filter fields
  const optionalValidators = ["extensionVersionFiltersVersion"];

  for (const key of optionalValidators) {
    const validator = FIELD_VALIDATORS[key];
    if (validator) {
      const fieldErrors = validator(data[key], data);
      if (fieldErrors && fieldErrors.length > 0) {
        newErrors[key] = fieldErrors;
      }
    }
  }

  // Add unique feature validation
  const uniqueValidation = validateUniqueFeature(experiments, data, {
    isCreate,
    isClone,
    fileData: data,
    extensionPrefix,
  });

  Object.entries(uniqueValidation).forEach(([key, error]) => {
    if (error) {
      newErrors[key] = [...(newErrors[key] || []), error];
    }
  });

  setErrors(newErrors);
  return Object.values(newErrors).every(
    (errorArray) => errorArray.length === 0,
  );
};
